import React, { useState } from "react";
import UserModel from "./types";
import { changePassword } from "./profile.service";

interface SecuritySectionProps {
  profileData: UserModel;
  onLogout: () => void;
}

const SecuritySection: React.FC<SecuritySectionProps> = ({
  profileData,
  onLogout,
}) => {
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current: "",
    new: "",
    confirm: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const validatePasswordForm = () => {
    if (!passwordForm.current) {
      setError("Current password is required");
      return false;
    }

    if (!passwordForm.new) {
      setError("New password is required");
      return false;
    }

    if (passwordForm.new.length < 8) {
      setError("New password must be at least 8 characters");
      return false;
    }

    if (passwordForm.new !== passwordForm.confirm) {
      setError("New passwords do not match");
      return false;
    }

    return true;
  };

  const handleSubmitPasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    setError(null);
    setSuccess(null);

    if (!validatePasswordForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await changePassword(
        profileData.email,
        passwordForm.current,
        passwordForm.new
      );

      setSuccess("Your password has been updated successfully");
      setPasswordForm({ current: "", new: "", confirm: "" });

      // Auto-close password change form after success
      setTimeout(() => {
        setIsChangingPassword(false);
        setSuccess(null);
      }, 3000);
    } catch (err) {
      setError(
        "Failed to update password. Please verify your current password and try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <h3 className="text-xl font-semibold mb-4">Security</h3>

      {/* Password Section */}
      <div className="mb-6">
        <h4 className="text-lg font-medium mb-2">Password</h4>

        {!isChangingPassword ? (
          <button
            onClick={() => setIsChangingPassword(true)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition"
          >
            Change Password
          </button>
        ) : (
          <form onSubmit={handleSubmitPasswordChange} className="space-y-4">
            {error && (
              <div className="p-3 text-sm bg-red-900/30 border border-red-700 text-red-200 rounded-lg">
                {error}
              </div>
            )}

            {success && (
              <div className="p-3 text-sm bg-green-900/30 border border-green-700 text-green-200 rounded-lg">
                {success}
              </div>
            )}

            <div>
              <label
                htmlFor="current"
                className="block text-sm font-medium text-gray-300 mb-1"
              >
                Current Password
              </label>
              <input
                id="current"
                name="current"
                type="password"
                value={passwordForm.current}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-400 text-white"
                required
              />
            </div>

            <div>
              <label
                htmlFor="new"
                className="block text-sm font-medium text-gray-300 mb-1"
              >
                New Password
              </label>
              <input
                id="new"
                name="new"
                type="password"
                value={passwordForm.new}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-400 text-white"
                required
                minLength={8}
              />
              <p className="mt-1 text-xs text-gray-400">
                Must be at least 8 characters long
              </p>
            </div>

            <div>
              <label
                htmlFor="confirm"
                className="block text-sm font-medium text-gray-300 mb-1"
              >
                Confirm New Password
              </label>
              <input
                id="confirm"
                name="confirm"
                type="password"
                value={passwordForm.confirm}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-400 text-white"
                required
              />
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition flex items-center"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Updating...
                  </>
                ) : (
                  "Update Password"
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setIsChangingPassword(false);
                  setError(null);
                  setSuccess(null);
                  setPasswordForm({ current: "", new: "", confirm: "" });
                }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg transition"
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Account Actions */}
      <div className="space-y-4">
        <h4 className="text-lg font-medium mb-2">Account Actions</h4>

        <button
          onClick={onLogout}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition w-full"
        >
          Log Out
        </button>
      </div>
    </div>
  );
};

export default SecuritySection;
